import React, {useCallback, useRef, MouseEvent} from 'react';
import cx from 'classnames';
import {maxPinnedColumns} from '../../consts/grid';
import {Column} from '../../../../stores/Form/Column';
import {HeaderCellSort} from '../HeaderCellSort';
import {HeaderCellFilter} from '../HeaderCellFilter';
import {PinIcon} from './parts/PinIcon';
import {addSort} from '../../actions/filters';
import {isActiveFilter} from '../../utils/filters';
import {BIDropdown} from '../BIDropdown';
import styles from '../../styles/HeaderCellContent.module.scss';

type Props = {
    tableId: string;
    column: Column;
    pinned?: boolean;
    onColumnsPin: (columnsCount: number) => void;
    isSorted: boolean;
    isActiveForm: boolean;
};

function onDragover(e) {
    e.preventDefault();
}

export const HeaderCellContent = ({
    column,
    pinned,
    onColumnsPin,
    tableId,
    isSorted,
    isActiveForm
}: Props) => {
    const {Mandatory, Title, id, parentForm} = column;
    const isDroppable = column.index + 1 <= maxPinnedColumns;

    const contentRef = useRef(null);
    const titleRef = useRef(null);

    const sortedColumn = parentForm.sort.find(item => Number(item.ColumnID) === id);

    const removeOverflow = useCallback(() => {
        const table = document.getElementById(tableId);
        if (table) table.classList.remove(styles.overflowHidden);
    }, [tableId]);

    const onDrop = useCallback(
        e => {
            onColumnsPin(column.index + 1);
            removeOverflow();
        },
        [removeOverflow, column, onColumnsPin]
    );

    const onDragStart = useCallback(() => {
        const table = document.getElementById(tableId);
        if (table) table.classList.add(styles.overflowHidden);
    }, [tableId]);

    const onDragEnd = useCallback(() => removeOverflow(), [removeOverflow]);

    const handleSort = useCallback(async () => {
        if (!parentForm.searchModeLevel && isActiveForm) {
            const sortData = {
                ColumnID: id,
                Desc: sortedColumn ? !sortedColumn.Desc : false
            };
            parentForm.resetSort();
            await addSort(parentForm, sortData);
            parentForm.setCurrentRow(0);
        }
    }, [id, parentForm, sortedColumn, isActiveForm]);

    const handleContentClick = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            if (contentRef.current === e.target || titleRef.current === e.target) {
                handleSort();
            }
        },
        [handleSort]
    );

    const getCellWrap = useCallback(() => contentRef.current, []);
    const hasActiveFilter = isActiveFilter(column);

    return (
        <div
            ref={contentRef}
            className={styles.headerCellContent}
            onDrop={isDroppable ? onDrop : undefined}
            onDragOver={isDroppable ? onDragover : undefined}
            aria-label="header-cell"
            onClick={handleContentClick}
        >
            {column.index === 0 && <BIDropdown componentClasses={styles.biIcon} column={column} />}

            <div className={styles.columnTitle} title={Title} data-test-id={id}>
                <span ref={titleRef} onClick={handleContentClick}>
                    {Title}
                </span>
            </div>

            {Mandatory && <div className={styles.requiredMark}>*</div>}

            {isSorted && (
                <HeaderCellSort
                    componentClasses={styles.sortIcon}
                    isDesc={!!sortedColumn?.Desc}
                    onClick={handleSort}
                />
            )}
            <div className={styles.cellToolbar}>
                <HeaderCellFilter
                    disabled={!isActiveForm}
                    componentClasses={cx(styles.filterContainer, {
                        [styles.isActive]: hasActiveFilter
                    })}
                    getCellWrap={getCellWrap}
                    column={column}
                    zIndex={10007}
                />

                {pinned && (
                    <PinIcon
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        componentClasses={styles.pinIcon}
                    />
                )}
            </div>
        </div>
    );
};
